<template>
  <div class="photoelectric-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$hν = W + ½mv_max²$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item" :class="canEmit ? 'cyan' : 'gray'">
            <label>单光子能量 (hν)</label>
            <span class="val">{{ photonEnergy.toFixed(2) }} <small>eV</small></span>
          </div>
          <div class="hud-item orange">
            <label>逸出功 (W_metal)</label>
            <span class="val">{{ workFunction.toFixed(2) }} <small>eV</small></span>
          </div>
          <div class="hud-item" :class="emitStatus.color">
             <label>发射状态</label>
             <span class="val" style="font-size: 11px">{{ emitStatus.text }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-photon"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <filter id="glow-electron"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 金属靶板 (阴极) -->
          <rect x="60" y="60" width="10" height="120" fill="#475569" rx="2" stroke="#1e293b" />
          <text x="50" y="120" text-anchor="end" fill="#475569" font-size="9" font-weight="900" transform="rotate(-90, 50, 120)">金属阴极 (Cesium)</text>

          <!-- 阳极板 -->
          <rect x="340" y="60" width="8" height="120" fill="#1e293b" rx="2" stroke="#334155" />

          <!-- 入射光子束 (Photons) -->
          <g v-for="p in incomingPhotons" :key="'p'+p.id">
             <path :d="`M ${p.x},${p.y} l 15,0`" fill="none" :stroke="spectralColor" stroke-width="2" stroke-dasharray="4,2" opacity="0.6">
                <animate attributeName="stroke-dashoffset" from="60" to="0" dur="0.2s" repeatCount="indefinite" />
             </path>
             <circle :cx="p.x" :cy="p.y" r="3" :fill="spectralColor" filter="url(#glow-photon)" />
          </g>

          <!-- 逸出的光电子 (Electrons) -->
          <g v-for="e in electrons" :key="'e'+e.id">
             <circle :cx="e.x" :cy="e.y" r="4" fill="#fbbf24" filter="url(#glow-electron)" />
             <!-- 电子运动矢量 -->
             <line :x1="e.x" :y1="e.y" :x2="e.x - 10" :y2="e.y" stroke="#fbbf24" stroke-width="1.5" opacity="0.4" />
          </g>

          <text x="210" y="40" fill="#334155" font-size="10" font-weight="900">真空玻璃容器 (Vacuum)</text>
        </svg>

        <div class="condition-lamp" :class="{active: canEmit}">
           {{ canEmit ? '【满足逸出条件】 电子已喷射' : '【能量不足】 低于红限频率，无电子逸出' }}
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row">
              <div class="control-group">
                 <label class="s-label">入射光波长 (λ) - 决定单光子能量</label>
                 <el-slider v-model="wavelength" :min="300" :max="800" size="small"></el-slider>
              </div>
              <div class="control-group">
                 <label class="s-label">光强 (Intensity) - 决定光子流量</label>
                 <el-slider v-model="intensity" :min="0" :max="100" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="info" size="mini" @click="wavelength = 400">紫光 (高能)</el-button>
                 <el-button type="danger" size="mini" @click="wavelength = 700">红光 (低能)</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-sunny"></i>
           <p><strong>核心揭示：</strong>光子是一份一份的。即便红光强度再大，单个光子能量不足以击出电子（$h\nu < W$）。<strong>观察：</strong>将波长滑块调至 500nm 以下，电子才会出现。且增加“光强”滑块，只会让电子变多，而不会让电子跑得更快，这完美解释了光的粒子性。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'PhotoelectricEffectViz',
  data() {
    return {
      wavelength: 600, // Starting with red-ish light (no emit)
      intensity: 50,
      workFunction: 2.14, // Cesium in eV
      incomingPhotons: [],
      electrons: [],
      nextId: 0,
      t: 0
    }
  },
  computed: {
    photonEnergy() { return 1240 / this.wavelength; }, // E = hc/lambda
    canEmit() { return this.photonEnergy > this.workFunction; },
    spectralColor() {
       const w = this.wavelength;
       if (w < 440) return '#a855f7';
       if (w < 490) return '#3b82f6';
       if (w < 570) return '#22c55e';
       if (w < 590) return '#eab308';
       if (w < 620) return '#f97316';
       return '#ef4444';
    },
    emitStatus() {
       if (this.canEmit) return { text: '√ 逸出有效', color: 'cyan' };
       return { text: '× 低于红限 (v < v0)', color: 'gray' };
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/紫外线|高能/.test(kp)) {
        this.wavelength = 350;
      } else if (/临界|红限|阈值/.test(kp)) {
        this.wavelength = 580; // Close to work function
      } else if (/红外|红光/.test(kp)) {
        this.wavelength = 750;
      }
    },
    animate() {
       this.t += 1;
       
       // Photon Generation
       if (this.t % Math.max(1, (110 - this.intensity) / 10) === 0) {
          this.incomingPhotons.push({ id: this.nextId++, x: 0, y: 70 + Math.random() * 100 });
       }
       
       // Update Photons
       this.incomingPhotons.forEach(p => {
          p.x += 6;
          // When photon hits metal (x=60)
          if (p.x >= 60 && p.x < 66 && this.canEmit) {
             this.electrons.push({ 
                id: this.nextId++, 
                x: 70, 
                y: p.y, 
                v: (this.photonEnergy - this.workFunction) * 2 // v depends on excess energy
             });
          }
       });
       this.incomingPhotons = this.incomingPhotons.filter(p => p.x < 360);
       
       // Update Electrons
       this.electrons.forEach(e => {
          e.x += e.v + 1.5; // Kinetic component + base speed
       });
       this.electrons = this.electrons.filter(e => e.x < 340);
       
       this.animId = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animId); }
}
</script>

<style scoped lang="scss">
.photoelectric-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fdf2f8; color: #be185d; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.gray { border-left: 3px solid #64748b; opacity: 0.6; } &.orange { border-left: 3px solid #fbbf24; } } }
.condition-lamp { position: absolute; bottom: 8px; width: 100%; text-align: center; font-size: 11px; font-weight: bold; color: #475569; &.active { color: #22c55e; text-shadow: 0 0 10px rgba(34,197,94,0.3); } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; gap: 20px; align-items: flex-end; .control-group { flex: 1; .s-label { font-size: 10px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; } } .btn-group { display: flex; gap: 4px; } } }
.discovery-card-compact { background: #fef2f2; padding: 10px 14px; border-radius: 10px; border: 1px solid #fecdd3; display: flex; align-items: center; gap: 10px; i { color: #e11d48; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 11px; color: #9f1239; line-height: 1.5; strong { color: #881337; } } }
</style>
